/**
 * FO-364 - opaque fout-referentie voor de generieke foutpagina's.
 *
 * De error-boundaries (SegmentError, global-error) tonen "ref: <id>" zodat een
 * gebruikersmelding ("er ging iets mis") herleidbaar is in App Insights of de
 * server-log. Twee bronnen, in volgorde:
 *
 *  1. De Next.js `error.digest` - aanwezig bij server-render-fouten, stabiel
 *     vanaf de eerste paint en door Next óók in de server-log geprint. De
 *     boundary stuurt de digest bovendien als property mee met het client-
 *     exception-event, dus `AppExceptions | where Properties['digest'] == ...`
 *     vindt 'm terug.
 *  2. De App Insights operation-id waaronder `trackClientException` het
 *     exception-event verstuurde - voor pure client-fouten (geen digest);
 *     terug te vinden via `AppExceptions | where OperationId == ...`.
 *
 * Beide zijn opaque id's: geen stack, geen foutdetail, geen PII.
 */

/** Kies de getoonde fout-referentie: server-digest wint, anders de AI-op-id. */
export function resolveFoutReferentie(
  digest: string | null | undefined,
  aiOperationId: string | null | undefined,
): string | null {
  if (typeof digest === 'string' && digest.length > 0) return digest
  if (typeof aiOperationId === 'string' && aiOperationId.length > 0) return aiOperationId
  return null
}

/**
 * Leest de actuele operation-/trace-id uit een Application Insights-instantie
 * (`context.telemetryTrace.traceID`) - de id waaronder een direct hierna
 * verstuurd telemetrie-event in `AppExceptions.OperationId` landt. Tolerant
 * getypeerd: elke afwijkende vorm (geen instantie, ontbrekende context, lege
 * string) levert `null` op, nooit een throw.
 */
export function operationIdVanAppInsights(ai: unknown): string | null {
  if (!ai || typeof ai !== 'object') return null
  const context = (ai as { context?: unknown }).context
  if (!context || typeof context !== 'object') return null
  const trace = (context as { telemetryTrace?: unknown }).telemetryTrace
  if (!trace || typeof trace !== 'object') return null
  const traceId = (trace as { traceID?: unknown }).traceID
  return typeof traceId === 'string' && traceId.length > 0 ? traceId : null
}
